import queryString from 'querystring';
import { push } from 'react-router-redux';
import { apiGet, apiPost, getHostApi, processError } from './apiWrapper';
import { updateObject, isNullOrUndefined, addToArray, removeFromArray, syncWithLocalStorage, fetchFromLocalStorage } from './storeFunctions';
import { languageChanged, userLogout } from './userContextStore';
import { toastNotification } from '../components/common/toast';
import { writeErr } from '../components/common/logger';

const LOCATION_CHANGE = '@@router/LOCATION_CHANGE'; // hardcoded to save 9KB lib link just to get one value

// Subject search
const searchSubjectsRequest = "SEARCH_SUBJECT_REQUEST";
export const searchSubjectsSucceeded = "SEARCH_SUBJECT_SUCCEEEED";
export const searchSubjectsFailed = "SEARCH_SUBJECT_FAILED";
const basicSearchFormToggle = "BASIC_SEARCH_FORM_TOGGLE";
export const hasSearchFilters = "SEARCH_FILTER_EXISTS";
const focusToQuickSearch = "QUICK_SEARCH_FOCUS_INPUT";
const focusedQuickSearch = "QUICK_SEARCH_FOCUSED";
const showQuickSearchResults = "SHOW_QUICK_SEARCH_RESULTS";
const hideQuickSearchResults = "HIDE_QUICK_SEARCH_RESULTS";
const changeEntityType = "CHANGE_ENTITY_TYPE";
const setSearchDomain = "SET_SEARCH_DOMAIN";
const resetSearchParams = "RESET_SEARCH_PARAMS";
const setSearchParams = "SET_SEARCH_PARAMS";
const searchFilterSet = "SEARCH_FILTER_EDIT";
const searchFiltersResetAll = "SEARCH_FILTERS_RESET_ALL";

// Custom search
const customSearchRequest = "CUSTOM_SEARCH_REQUEST";
const customSearchSucceeded = "CUSTOM_SEARCH_SUCCEEDED";
const customSearchFailed = "CUSTOM_SEARCH_FAILED";

// Clipboard
const addToClipboard = "ADD_TO_CLIPBOARD";
const removeFromClipboard = "REMOVE_FROM_CLIPBOARD";
const clearClipboard = "CLEAR_CLIPBOARD";
const sortClipboard = "SORT_CLIPBOARD";

// Pdf export
const addToPdfExport = "ADD_TO_PDF_EXPORT";
const removeFromPdfExport = "REMOVE_FROM_PDF_EXPORT";
const setAllToPdfExport = "SET_ALL_TO_PDF_EXPORT"; 
const addIdsToPdfExport = "ADD_IDS_TO_PDF_EXPORT"; 
const removeIdsFromPdfExport = "REMOVE_ALL_FROM_PDF_EXPORT"; 
const pdfExportRequest = "PDF_EXPORT_REQUEST"; 
const pdfExportError = "PDF_EXPORT_ERROR"; 
const resetPdfExport = "RESET_PDF_EXPORT";

// Liens search
const searchLiensRequest = "SEARCH_LIENS_REQUEST";
const searchLiensSucceeded = "SEARCH_LIENS_SUCCEEEED";
const searchLiensFailed = "SEARCH_LIENS_FAILED";
const initLiensSearchRequest = "INIT_LIENS_SEARCH_REQUEST";
const initLiensSearchSucceeded = "INIT_LIENS_SEARCH_SUCCEEED";
const initLiensSearchFailed = "INIT_LIENS_SEARCH_FAILED";
const leaveLiensSearch = "LIENS_SEARCH_LEAVE";

// Bankruptcies search
const initBankruptciesSearchRequest = "INIT_BANKRUPTCIES_SEARCH_REQUEST";
const searchBankruptciesRequest = "SEARCH_BANKRUPTCIES_REQUEST";
const searchBankruptciesSucceeded = "SEARCH_BANKRUPTCIES_SUCCEEEED";
const searchBankruptciesFailed = "SEARCH_BANKRUPTCIES_FAILED";
const leaveBankruptciesSearch = "BANKRUPTCIES_SEARCH_LEAVE";
const bankruptciesToggleSearchForm = "BANKRUPTCIES_TOGGLE_SEARCH_FORN";
const bankruptciesSetPage = "BANKRUPTCIES_SET_PAGE";

// Bulletins search
const searchBulletinsRequest = "SEARCH_BULLETINS_REQUEST";
const searchBulletinsSucceeded = "SEARCH_BULLETINS_SUCCEEEED";
const searchBulletinsFailed = "SEARCH_BULLETINS_FAILED";
const leaveBulletinsSearch = "BULLETINS_SEARCH_LEAVE";

// CUZK search
const cuzkSearchReset = "SEARCH_CUZK_RESET";
const cuzkSearchRequest = "SEARCH_CUZK_REQUEST";
const cuzkSearchSucceeded = "SEARCH_CUZK_SUCCEEEED";
const cuzkSearchFailed = "SEARCH_CUZK_FAILED";
const cuzkSetPageAction = "SET_CUZK_PAGE";
const cuzkExpandAction = "SET_CUZK_EXPAND";
const cuzkPdfRequest = "SET_CUZK_PDF_REQUEST";
const cuzkPdfError = "SET_CUZK_PDF_ERROR";
const cuzkPageSize = 10;

// PropertySk search
const propertySkSearchReset = "PROPERTYSK_RESET";
const propertySkSearchRequest = "PROPERTYSK_REQUEST";
const propertySkSearchSucceeded = "PROPERTYSK_SEARCH_SUCCEEEED";
const propertySkSearchFailed = "PROPERTYSK_SEARCH_FAILED";

export const searchStatus = {
    idle: 0,
    requested: 1,
    success: 2,
    error: 3
};

export const customSearchType = {
    linkAction: "linkAction",
    basicSearchLabels: "basicSearchLabels"
};

export const searchType = {
    QS: "qs",
    BS: "bs"
};

const initialCustomSearchState = {
    status: searchStatus.idle,
    data: null
};

export const cuzkSearchType = {
    requests: "requests",
    subjects: "subjects",
    report: "report",
    detail: "detail",
    ownershipDeed: "ownershipDeed"
};

export const cuzkExpandType = {
    cadastralUnits: "cadastralUnits",
    ownershipDocuments: "ownershipDocuments",
    parcels: "parcels",
    buildings: "buildings",
    flats: "flats",
};

export const cuzkExportPdfType = {
    expanded: 0,
    purchased: 1,
    searchResult: 2
};

const initialCuzkState = {
    [cuzkSearchType.requests]: { ...initialCuzkItemState }, 
    [cuzkSearchType.subjects]: { ...initialCuzkItemState, page: 1, pageSize: cuzkPageSize },
    [cuzkSearchType.report]: { ...initialCuzkItemState, expandedData: null },
    [cuzkSearchType.detail]: {}
};

const initialCuzkItemState = {
    status: searchStatus.idle,
    params: null,
    data: null,
    error: null
};

export const propertySkSearchBy = {
    companyOwners: 1,
    personOwners: 2,
    companyOwnerDeeds: 3,
    personOwnerDeeds: 4,
    ownershipDeed: 5,
    registrationNumber: 6,
    landId: 7,
    flatNumber: 8,
    document: 9,
    documentCompany: 10,
    documentPerson: 11,
    odDetail: 12
};

const initialPropertySkState = {
    status: searchStatus.idle,
    params: null,
    data: null,
    error: null,
    cadastreUnitData: null
};

const initialQuickSearchResultHidde = {
    navMenuDesktop: false,
    navMenuMobile: false,
    mainSearch: false
}

export const defaultPaging = {
    page: 1,
    count: 30,
    offset: 0
}

export const defaultOrderBy = {
    orderCol: "Relevance",
    orderDesc: true
}

const defaultExportSettings = {
    all: false,
    ids: [], //represents included or excluded ids depending on the value of the "checked all",
    error: null,
    isEnabled: false
}

const initialState = {
    isSearching: false,
    searchPaging: { ...defaultPaging },
    searchParams: null,
    searchOrderBy: { ...defaultOrderBy },
    searchType: null,
    searchDomains: null,
    searchResult: null,
    basicFormExpanded: false,
    basicFormWasExpanded: false,
    searchFilterHasData: false,
    error: null,
    focusInput: false,
    linkAction: { ...initialCustomSearchState },
    basicSearchLabels: { ...initialCustomSearchState },
    quickSearchResults: { ...initialQuickSearchResultHidde },
    isPo: true,
    cuzk: null,
    propertySk: null,
    languageChanged: false,
    searchFilter: {},
    clipboard: fetchFromLocalStorage('subject_clipboard', []),
    clipboardCounter: initClipboardCounter(fetchFromLocalStorage('subject_clipboard', [])),
    exportSettings: { ...defaultExportSettings }
};

export const searchActions = {
    backToSearch: () => async (dispatch, getState) => dispatch({ type: LOCATION_CHANGE, payload: { pathname: '/search', hash: '', search: '' } }),
    redirect: (url) => async (dispatch) => dispatch(push(url)),

    // Subject search
    changePage: (page) => async (dispatch, getState) => changePage(page, dispatch, getState),
    changeSort: (orderCol, orderDesc) => async (dispatch, getState) => changeSort(orderCol, orderDesc, dispatch, getState),
    repeatSearch: () => async (dispatch, getState) => repeatSearch(dispatch, getState),
    getSubjects: (searchType, params, paging, orderBy, domains) => async (dispatch, getState) => searchSubjects(searchType, params, paging || getState().search.searchPaging, orderBy || getState().search.searchOrderBy, domains || getState().search.searchDomains, dispatch, getState),
    toggleBasicForm: (val) => async (dispatch) => dispatch({ type: basicSearchFormToggle, val }),
    focusQuickSearch: () => async (dispatch) => dispatch({ type: focusToQuickSearch }),
    focusedQuickSearch: () => async (dispatch) => dispatch({ type: focusedQuickSearch }),
    showQuickSearchResults: (subId) => async (dispatch) => dispatch({ type: showQuickSearchResults, subId }),
    hideQuickSearchResults: (subId) => async (dispatch) => dispatch({ type: hideQuickSearchResults, subId }),
    triggerChangeEntityType: (value) => async (dispatch) => dispatch({ type: changeEntityType, value }),
    setSearchDomain: (value) => async (dispatch) => dispatch({ type: setSearchDomain, value }),
    resetSearchParams: () => async (dispatch) => dispatch({ type: resetSearchParams }),
    setSearchParams: (params) => async (dispatch) => dispatch({ type: setSearchParams, params }),
    setSearchFilter: (key, data) => async (dispatch) => dispatch({ type: searchFilterSet, key: key, data: data }),
    resetSearchFilters: () => async (dispatch) => dispatch({ type: searchFiltersResetAll }),

    // Custom search
    customSearch: (controller, url, method, data, searchType) => async (dispatch) => performCustomSearch(controller, url, method, data, searchType, dispatch),

    // Clipboard
    addToClipboard: (data, toastLabel) => async (dispatch) => {
        toastNotification({ description: `${toastLabel}: ${data.name}`, color: 'info', hideProgressBar: false, autoCloseAfter: 2500 })
        dispatch({ type: addToClipboard, data: data });
    },
    removeFromClipboard: (data, toastLabel) => async (dispatch) => {
        toastNotification({ description: `${toastLabel}: ${data.name}`, color: 'info', hideProgressBar: false, autoCloseAfter: 2500 })
        dispatch({ type: removeFromClipboard, data: data })
    },
    clearClipboard: (toastLabel) => async (dispatch) => {
        toastNotification({ description: toastLabel, color: 'info', hideProgressBar: false, autoCloseAfter: 2500 })
        dispatch({ type: clearClipboard })
    },
    sortClipboard: (sortFunction, toastLabel) => async (dispatch) => {
        toastNotification({ description: toastLabel, color: 'info', hideProgressBar: false, autoCloseAfter: 2500 })
        dispatch({ type: sortClipboard, sortFunction: sortFunction })
    },

    // Pdf export
    addToPdfExport: (id) => async (dispatch) => dispatch({ type: addToPdfExport, id: id }),
    removeFromPdfExport: (id) => async (dispatch) => dispatch({ type: removeFromPdfExport, id: id }),
    setAllToPdfExport: (value) => async (dispatch) => dispatch({ type: setAllToPdfExport, value: value }),
    addIdsToPdfExport: (ids) => async (dispatch) => dispatch({ type: addIdsToPdfExport, ids: ids }),
    removeIdsFromPdfExport: (ids) => async (dispatch) => dispatch({ type: removeIdsFromPdfExport, ids: ids }),
    resetPdfExport: () => async (dispatch) => dispatch({ type: resetPdfExport }),

    // Liens search
    initLiensSearch: () => async (dispatch) => initLiensSearch(dispatch),
    getLiens: (params) => async (dispatch, getState) => searchLiens(params, dispatch, getState),
    endLiensSearch: () => async (dispatch) => dispatch({ type: leaveLiensSearch }),
    changePageLiens: (page) => async (dispatch, getState) => changePageLiens(page, dispatch, getState),
    exportLiensSearchPdf: () => async (dispatch, getState) => exportLiensSearchPdf(getState),

    // Bankruptcies search
    initBankruptciesSearch: () => async (dispatch) => initBankruptciesSearch(dispatch),
    getBankruptcies: (params) => async (dispatch, getState) => searchBankruptcies(params, dispatch, getState),
    endBankruptciesSearch: () => async (dispatch) => dispatch({ type: leaveBankruptciesSearch }),
    toggleBankruptciesSearchForm: () => async (dispatch, getState) => dispatch({ type: bankruptciesToggleSearchForm, value: !getState().search.displaySearchForm || false }),
    changePageBankruptcy: (page) => async (dispatch, getState) => changePageBankruptcy(page, dispatch, getState),
    exportBankruptciesSearchPdf: () => async (dispatch, getState) => exportBankruptciesSearchPdf(getState),

    // Bulletins search
    getBusinessBulletin: (entityId, params, paging) => async (dispatch, getState) => searchBusinessBulletins(entityId, params, paging, dispatch, getState),
    endBulletinsSearch: () => async (dispatch) => dispatch({ type: leaveBulletinsSearch }),
    changePageBulletin: (entityId, page) => async (dispatch, getState) => changePageBulletin(entityId, page, dispatch, getState),
    exportBusinessBulletinSearchPdf: (entityId) => async (dispatch, getState) => exportBusinessBulletinSearchPdf(entityId, getState),

    // CUZK search
    cuzkReset: (searchType) => async (dispatch) => dispatch({ type: cuzkSearchReset, cuzkSearchType: searchType }),
    cuzkInitiateRequest: (params, resultAction) => async (dispatch) => performCuzkSearch(params, resultAction, dispatch),
    cuzkUpdateOwnershipDeedSearch: (params, resultAction) => async (dispatch) => updateOwnershipDeedSearch(params, resultAction, dispatch),
    cuzkGetRequest: (params, resultAction, reportAction) => async (dispatch) => performCuzkGetRequest(params, resultAction, reportAction, dispatch),
    cuzkGetOwnershipDeedRequest: (params) => async (dispatch) => performCuzkGetOwnershipDeedRequest(params, dispatch), 
    cuzkGetOwnershipDetailAsReport: (params) => async (dispatch) => performCuzkGetOwnershipDetailAsReport(params, true, dispatch),
    cuzkUpdateOwnershipDetailAsReport: (params) => async (dispatch) => performCuzkGetOwnershipDetailAsReport(params, false, dispatch),
    cuzkSearchOwnership: (params) => async (dispatch) => performCuzkOwnershipSearch(params, true, dispatch),
    cuzkUpdateOwnershipData: (params) => async (dispatch) => performCuzkOwnershipSearch(params, false, dispatch),
    cuzkSearchOwnershipDetail: (params) => async (dispatch) => performCuzkOwnershipDetailSearch(params, true, dispatch),
    cuzkUpdateOwnershipDetailData: (params) => async (dispatch) => performCuzkOwnershipDetailSearch(params, false, dispatch),
    cuzkSetPage: (page) => async (dispatch) => dispatch({ type: cuzkSetPageAction, page: page }),
    cuzkExpand: (expandType, cadastreUnitId, documentId, itemId, value) => async (dispatch) => dispatch({ type: cuzkExpandAction, expandType: expandType, cadastreUnitId: cadastreUnitId, documentId: documentId, itemId: itemId, expanded: value, dispatch }),
    cuzkExportReportPdf: (type, isSearchOwnershipDeed) => async (dispatch, getState) => performCuzkExportReportPdf(type, isSearchOwnershipDeed, getState, dispatch),
    cuzkExportSearchResultPdf: () => async (dispatch, getState) => performCuzkExportSearchResultPdf(getState, dispatch),

    // PropertySk search
    propertySkSearch: (params, onSuccess) => async (dispatch) => performPropertySkSearch(params, onSuccess, dispatch),
    propertySkReset: () => async (dispatch) => dispatch({ type: propertySkSearchReset }),
    propertySkExportPdf: (withCheck) => async (dispatch, getState) => performPropertySkExportPdf(withCheck, getState, dispatch),
}

export const reducer = (state, action) => {
    state = state || initialState;
    const bankruptcyPageSize = 10;

    switch (action.type) {
        case LOCATION_CHANGE:
            return updateObject(state, { location: action.payload.pathname, search: action.payload.search, quickSearchResults: { ...initialQuickSearchResultHidde } });
        case 'STORAGE_CHANGED':
            if (action.key !== 'subject_clipboard') {
                return state;
            }
            const data = fetchFromLocalStorage('subject_clipboard', state.clipboard);
            if (JSON.stringify(data) === JSON.stringify(state.clipboard)) {
                return state;
            }
            return updateObject(state, { clipboard: data });
        case 'LOGIN_EXPIRED':
        case 'USER_LOGOUT':
            return updateObject(state, { clipboard: syncWithLocalStorage('subject_clipboard', []) });
        case languageChanged:
            return { ...state, languageChanged: true };

        // Subject search
        case searchSubjectsRequest:
            return {
                ...state, isSearching: true, searchParams: action.params, searchPaging: action.paging, searchType: action.searchType, searchDomains: action.domains, searchResult: null, error: null,
                basicSearchLabels: state.basicSearchLabels, linkAction: state.linkAction, languageChanged: false
            };
        case searchSubjectsSucceeded:
            return { ...state, isSearching: false, searchResult: action.data, basicSearchLabels: state.basicSearchLabels, linkAction: state.linkAction };
        case searchSubjectsFailed:
            return { ...state, isSearching: false, error: action.error, basicSearchLabels: state.basicSearchLabels, linkAction: state.linkAction };
        case basicSearchFormToggle:
            return { ...state, basicFormExpanded: action.val, basicFormWasExpanded: true };
        case hasSearchFilters:
            return { ...state, searchFilterHasData: action.val };
        case focusToQuickSearch:
            return { ...state, basicFormExpanded: false, focusInput: true };
        case focusedQuickSearch:
            return { ...state, focusInput: false };
        case showQuickSearchResults:
            return { ...state, quickSearchResults: { ...state.quickSearchResults, [action.subId]: true } };
        case hideQuickSearchResults:
            return { ...state, quickSearchResults: { ...state.quickSearchResults, [action.subId]: false } };
        case changeEntityType:
            return { ...state, isPo: action.value, quickSearchResults: { ...initialQuickSearchResultHidde } };
        case setSearchDomain:
            return { ...state, searchDomains: action.value };
        case resetSearchParams:
        case userLogout:
            return { ...state, searchParams: null }
        case setSearchParams:
            return { ...state, searchParams: action.params }
        case searchFilterSet:
            return { ...state, searchFilter: { ...state.searchFilter, [action.key]: action.data } }
        case searchFiltersResetAll:
            return { ...state, searchFilter: {} }

        // search action search - various searches
        case customSearchRequest:
            return { ...state, [action.searchType]: { ...initialCustomSearchState, status: searchStatus.requested } };
        case customSearchSucceeded:
            return { ...state, [action.searchType]: { ...initialCustomSearchState, status: searchStatus.success, data: action.data.data } };
        case customSearchFailed:
            return { ...state, [action.searchType]: { ...initialCustomSearchState, status: searchStatus.error, error: action.err } };

        // Clipboard
        case addToClipboard:
            return { ...state, clipboard: syncWithLocalStorage('subject_clipboard', addToClipboardArray([...state.clipboard], action.data, state.clipboardCounter, 'entityId')), clipboardCounter: (state.clipboardCounter + 1) };
        case removeFromClipboard:
            return { ...state, clipboard: syncWithLocalStorage('subject_clipboard', removeFromArray([...state.clipboard], action.data, 'entityId')) };
        case clearClipboard:
            return { ...state, clipboard: syncWithLocalStorage('subject_clipboard', []) };
        case sortClipboard:
            return { ...state, clipboard: syncWithLocalStorage('subject_clipboard', [...state.clipboard].sort(action.sortFunction)) };

        // Pdf export
        case addToPdfExport: 
            return { ...state, exportSettings: { ...state.exportSettings, ids: state.exportSettings.ids.concat(action.id), error: null } };
        case removeFromPdfExport: 
            return { ...state, exportSettings: { ...state.exportSettings, ids: state.exportSettings.ids.filter(x => x !== action.id), error: null } };
        case setAllToPdfExport: 
            return { ...state, exportSettings: { ...state.exportSettings, all: action.value, ids: [], error: null } };
        case addIdsToPdfExport:
            return { ...state, exportSettings: { ...state.exportSettings, all: false, ids: action.ids } };
        case removeIdsFromPdfExport:
            return { ...state, exportSettings: { ...state.exportSettings, all: false, ids: state.exportSettings.ids.filter(x => !action.ids.includes(x)) } };
        case pdfExportRequest:
            return { ...state, exportSettings: { ...state.exportSettings, error: null }};
        case pdfExportError:
            return { ...state, exportSettings: { ...state.exportSettings, error: action.error }};
        case resetPdfExport:
            return { ...state, exportSettings: defaultExportSettings };


        // Liens search
        case initLiensSearchRequest:
            return { ...state, searchingLiensSubjectTypes: true, liensSubjectTypes: null, page: 1, pageSize: 1, languageChanged: false };
        case initLiensSearchSucceeded:
            return { ...state, isSearching: false, searchingLiensSubjectTypes: false, liensSubjectTypes: action.data.data };
        case initLiensSearchFailed:
            return { ...state, isSearching: false, searchingLiensSubjectTypes: false, error: action.error };
        case searchLiensRequest:
            return { ...state, isSearching: true, searchResult: null, error: null, searchParams: action.params, page: action.params.page, languageChanged: false }
        case searchLiensSucceeded:
            return { ...state, isSearching: false, searchResult: action.data, error: null }
        case searchLiensFailed:
            return { ...state, isSearching: false, searchResult: null, error: action.error };
        case leaveLiensSearch:
            return initialState;

        // Bankruptcies search
        case initBankruptciesSearchRequest:
            return { ...initialState, displaySearchForm: true, pageSize: bankruptcyPageSize, languageChanged: false };
        case searchBankruptciesRequest:
            return { ...state, isSearching: true, searchResult: null, error: null, searchParams: action.params, page: action.params.page, pageSize: bankruptcyPageSize, languageChanged: false };
        case searchBankruptciesSucceeded:
            return { ...state, isSearching: false, searchResult: action.data, error: null, displaySearchForm: shouldDisplayBankruptciesSearchForm(action, state), pageSize: bankruptcyPageSize };
        case searchBankruptciesFailed:
            return { ...state, isSearching: false, searchResult: null, error: action.error, displaySearchForm: true };
        case bankruptciesToggleSearchForm:
            return { ...state, displaySearchForm: action.value, pageSize: bankruptcyPageSize };
        case bankruptciesSetPage:
            return { ...state, page: action.page, pageSize: bankruptcyPageSize };
        case leaveBankruptciesSearch:
            return initialState;

        // Bulletins search
        case searchBulletinsRequest:
            return { ...state, isSearching: true, searchResult: null, error: null, searchParams: action.params, searchPaging: action.paging, languageChanged: false };
        case searchBulletinsSucceeded:
            return { ...state, isSearching: false, searchResult: action.data, error: null };
        case searchBulletinsFailed:
            return { ...state, isSearching: false, searchResult: null, error: action.error };
        case leaveBulletinsSearch:
            return initialState;

        // CUZK search
        case cuzkSearchReset: {
            switch (action.cuzkSearchType) {
                case cuzkSearchType.requests: return { ...state, cuzk: initialCuzkState };
                case cuzkSearchType.subjects: return { ...state, cuzk: { ...initialCuzkState, [cuzkSearchType.requests]: state.cuzk[cuzkSearchType.requests] } };
                case cuzkSearchType.report: return { ...state, cuzk: { ...state.cuzk, [cuzkSearchType.report]: { ...initialCuzkItemState, expandedData: null }, [cuzkSearchType.detail]: {} } };
                case cuzkSearchType.detail: return { ...state, cuzk: { ...state.cuzk, [cuzkSearchType.detail]: {} } };
                default: throw `Unknown CUZK search type ${action.cuzkSearchType}`;
            }
        }
        case cuzkSearchRequest: {
            switch (action.cuzkSearchType) {
                case cuzkSearchType.requests: return {
                    ...state, cuzk: {
                        ...initialCuzkState, [cuzkSearchType.requests]: 
                            { ...initialCuzkItemState, status: searchStatus.requested, params: action.params}
                    }
                }
                case cuzkSearchType.subjects: return {
                    ...state, cuzk: {
                        [cuzkSearchType.requests]: isNullOrUndefined(state.cuzk) || isNullOrUndefined(state.cuzk[cuzkSearchType.requests])
                            ? initialCuzkItemState : state.cuzk[cuzkSearchType.requests],
                        [cuzkSearchType.subjects]: { ...initialCuzkItemState, status: searchStatus.requested, params: action.params, page: 1, pageSize: cuzkPageSize },
                        [cuzkSearchType.report]: { ...initialCuzkItemState },
                        [cuzkSearchType.detail]: {}
                    }
                };
                case cuzkSearchType.report: return {
                    ...state, cuzk: {
                        [cuzkSearchType.requests]: isNullOrUndefined(state.cuzk) || isNullOrUndefined(state.cuzk[cuzkSearchType.requests])
                            ? initialCuzkItemState : state.cuzk[cuzkSearchType.requests],
                        [cuzkSearchType.subjects]: isNullOrUndefined(state.cuzk) || isNullOrUndefined(state.cuzk[cuzkSearchType.subjects])
                            ? { ...initialCuzkItemState, page: 1, pageSize: cuzkPageSize } : state.cuzk[cuzkSearchType.subjects],
                        [cuzkSearchType.report]: { ...initialCuzkItemState, status: searchStatus.requested, params: action.params, expandedData: null },
                        [cuzkSearchType.detail]: {}
                    }
                };
                case cuzkSearchType.ownershipDeed: return {
                    ...state, cuzk: {
                        [cuzkSearchType.report]: { ...initialCuzkItemState, status: searchStatus.requested, params: action.params, expandedData: null },
                        [cuzkSearchType.detail]: {}
                    }
                };
                case cuzkSearchType.detail: return {
                    ...state, cuzk: {
                        ...state.cuzk, [cuzkSearchType.detail]:
                            { ...state.cuzk[cuzkSearchType.detail], [action.documentId]: { error: null, data: null, expandedData: null } }
                    }
                };
                default: throw `Unknown CUZK search type ${action.cuzkSearchType}`;
            }
        }
        case cuzkSearchSucceeded: {
            switch (action.cuzkSearchType) {
                case cuzkSearchType.requests:
                case cuzkSearchType.subjects:
                case cuzkSearchType.report:
                    return {
                        ...state, cuzk: {
                            ...state.cuzk, [action.cuzkSearchType]:
                            {
                                ...state.cuzk[action.cuzkSearchType], status: searchStatus.success, error: null, data: action.data.data,
                                expandedData: action.expandedData
                            }
                        }
                    };
                case cuzkSearchType.detail:
                    return {
                        ...state, cuzk: {
                            ...state.cuzk, [cuzkSearchType.detail]: {
                                ...state.cuzk[cuzkSearchType.detail], [action.documentId]:
                                    { error: null, data: action.data.data, expandedData: action.expandedData }
                            }
                        }
                    };
                case cuzkSearchType.ownershipDeed:
                    return {
                        ...state, cuzk: {
                            ...state.cuzk, [cuzkSearchType.report]:
                            {
                                ...state.cuzk[cuzkSearchType.report], status: searchStatus.success, error: null, data: action.data.data,
                                expandedData: action.expandedData
                            },
                            [cuzkSearchType.detail]: {
                                ...state.cuzk[cuzkSearchType.detail], [action.documentId]:
                                    { error: null, data: action.data.data, expandedData: action.expandedDataDetail }
                            }
                        }
                    };
                default: throw `Unknown CUZK search type ${action.cuzkSearchType}`;
            }
        }
        case cuzkSearchFailed: {
            switch (action.cuzkSearchType) {
                case cuzkSearchType.requests:
                case cuzkSearchType.subjects:
                case cuzkSearchType.ownershipDeed:
                case cuzkSearchType.report:
                    return {
                        ...state, cuzk: {
                            ...state.cuzk, [action.cuzkSearchType]:
                                { ...state.cuzk[action.cuzkSearchType], status: searchStatus.error, error: action.error, data: null, expandedData: null }
                        }
                    };
                case cuzkSearchType.detail:
                    return {
                        ...state, cuzk: {
                            ...state.cuzk, [cuzkSearchType.detail]: {
                                ...state.cuzk[cuzkSearchType.detail], [action.documentId]:
                                    { error: action.error, data: null, expandedData: null }
                            }
                        }
                    };
                default: throw `Unknown CUZK search type ${action.cuzkSearchType}`;
            }
        }
        case cuzkSetPageAction:
            return { ...state, cuzk: { ...state.cuzk, [cuzkSearchType.subjects]: { ...state.cuzk[cuzkSearchType.subjects], page: action.page } } };
        case cuzkExpandAction:
            switch (action.expandType) {
                case cuzkExpandType.cadastralUnits:
                    return {
                        ...state, cuzk: {
                            ...state.cuzk, [cuzkSearchType.report]: {
                                ...state.cuzk[cuzkSearchType.report], expandedData:
                                    { ...state.cuzk[cuzkSearchType.report].expandedData, [action.cadastreUnitId]: { ...state.cuzk[cuzkSearchType.report].expandedData[action.cadastreUnitId], expanded: action.expanded } }
                            }
                        }
                    };
                case cuzkExpandType.ownershipDocuments:
                    return {
                        ...state, cuzk: {
                            ...state.cuzk, [cuzkSearchType.report]: {
                                ...state.cuzk[cuzkSearchType.report], expandedData:
                                {
                                    ...state.cuzk[cuzkSearchType.report].expandedData, [action.cadastreUnitId]: {
                                        ...state.cuzk[cuzkSearchType.report].expandedData[action.cadastreUnitId], [cuzkExpandType.ownershipDocuments]:
                                            { ...state.cuzk[cuzkSearchType.report].expandedData[action.cadastreUnitId][cuzkExpandType.ownershipDocuments], [action.documentId]: action.expanded }
                                    }
                                }
                            }
                        }
                    };
                case cuzkExpandType.parcels:
                case cuzkExpandType.buildings:
                case cuzkExpandType.flats:
                    return {
                        ...state, cuzk: {
                            ...state.cuzk, [cuzkSearchType.detail]: {
                                ...state.cuzk[cuzkSearchType.detail], [action.documentId]:
                                {
                                    ...state.cuzk[cuzkSearchType.detail][action.documentId], expandedData: {
                                        ...state.cuzk[cuzkSearchType.detail][action.documentId].expandedData, [action.expandType]:
                                            { ...state.cuzk[cuzkSearchType.detail][action.documentId].expandedData[action.expandType], [action.itemId]: action.expanded }
                                    }
                                }
                            }
                        }
                    };
            }
        case cuzkPdfRequest:
            return { ...state, cuzk: { ...state.cuzk, [cuzkSearchType.report]: { ...state.cuzk[cuzkSearchType.report], error: null } } };
        case cuzkPdfError:
            return { ...state, cuzk: { ...state.cuzk, [cuzkSearchType.report]:
                { ...state.cuzk[cuzkSearchType.report], error: isNullOrUndefined(action.message) ? "Common.Error": action.message } } };

        // PropertySk search
        case propertySkSearchReset:
            return { ...state, propertySk: { ...initialPropertySkState } };
        case propertySkSearchRequest: {
            if (action.params.searchBy !== propertySkSearchBy.odDetail)
                return { ...state, propertySk: { ...initialPropertySkState, status: searchStatus.requested, params: action.params }, languageChanged: false };
            else {
                let cadastreUnitData = isNullOrUndefined(state.propertySk.cadastreUnitData) ? {} : state.propertySk.cadastreUnitData;
                return {
                    ...state, propertySk: {
                        ...state.propertySk, cadastreUnitData:
                            { ...cadastreUnitData, [action.cadastreUnitId]: { data: null, error: null, status: searchStatus.requested } }
                    }, languageChanged: false
                }
            }
        };
        case propertySkSearchSucceeded: {
            const hasError = !isNullOrUndefined(action.data.error) && !isNullOrUndefined(action.data.error.code) && action.data.error.code !== 200 ? true : false;
            if (action.searchBy !== propertySkSearchBy.odDetail)
                return {
                    ...state, propertySk:
                        { ...state.propertySk, data: action.data.data, error: action.data.error, status: hasError ? searchStatus.error : searchStatus.success }
                };
            else {
                return {
                    ...state, propertySk: {
                        ...state.propertySk, cadastreUnitData:
                        {
                            ...state.propertySk.cadastreUnitData, [action.cadastreUnitId]:
                                { data: action.data.data, error: action.data.error, status: hasError ? searchStatus.error : searchStatus.success }
                        }
                    }
                }
            }
        };
        case propertySkSearchFailed: {
            if (action.searchBy !== propertySkSearchBy.odDetail)
                return { ...state, propertySk: { ...state.propertySk, error: action.error, status: searchStatus.error } };
            else {
                return {
                    ...state, propertySk: {
                        ...state.propertySk, cadastreUnitData:
                            { ...state.propertySk.cadastreUnitData, [action.cadastreUnitId]: { data: null, error: action.error, status: searchStatus.error } }
                    }
                }
            }
        }

        // None
        default:
            return state;
    }
}

function getSearchUrl(state, location) {
    return `${window.babylonApiUrl}/api/search/${location}?${queryString.stringify({ ...state.search.searchParams, ...state.search.searchPaging })}`;
}

function shouldDisplayBankruptciesSearchForm(action, state) {
    return state.searchParams.page > 1 ? state.displaySearchForm : isNullOrUndefined(action.data) || isNullOrUndefined(action.data.data) ||action.data.data.found === 0
}

function changePage(page, dispatch, getState) {
    const oldState = getState().search;
    const paging = { ...oldState.searchPaging, page, offset: (page - 1) * oldState.searchPaging.count };
    searchSubjects(oldState.searchType, oldState.searchParams, paging, oldState.searchOrderBy, oldState.searchDomains, dispatch, getState);
}

function changePageBankruptcy(page, dispatch, getState) {
    const params = getState().search.searchParams;
    params.page = page;
    searchBankruptcies(params, dispatch, getState);
}

function changePageBulletin(entityId, page, dispatch, getState) {
    const oldState = getState().search;
    const paging = { ...oldState.searchPaging, page };
    searchBusinessBulletins(entityId, oldState.searchParams, paging, dispatch, getState);
}

function changePageLiens(page, dispatch, getState) {
    const params = getState().search.searchParams;
    params.page = page;
    searchLiens(params, dispatch, getState);
}

function changeSort(orderCol, orderDesc, dispatch, getState) {
    const oldState = getState().search;
    oldState.searchOrderBy = { orderCol, orderDesc };

    searchSubjects(oldState.searchType, oldState.searchParams, oldState.searchPaging, oldState.searchOrderBy, oldState.searchDomains, dispatch, getState);
}

function repeatSearch(dispatch, getState) {
    const oldState = getState().search;
    searchSubjects(oldState.searchType, oldState.searchParams, oldState.searchPaging, oldState.searchOrderBy, oldState.searchDomains, dispatch, getState);
}

function searchSubjects(searchType, params, paging, orderBy, domains, dispatch, getState) {
    dispatch({ type: searchSubjectsRequest, searchType: searchType, params, paging, domains });
    dispatch({ type: hasSearchFilters, val: false });
    if (searchType === "qs") {
        apiGet(`${window.babylonApiUrl}/api/search/quick`, { query: params.name, offset: paging.offset, size: paging.count },
            res => getState().search.isSearching && dispatch({ type: searchSubjectsSucceeded, data: res }),
            err => dispatch({ type: searchSubjectsFailed, error: err })
        );
    }
    else if (searchType === "bs") {
        apiPost(`${window.babylonApiUrl}/api/search/basic`, {}, { ...params, ...paging, ...orderBy },
            res => {
                dispatch({ type: searchSubjectsSucceeded, data: res });
                dispatch({ type: hasSearchFilters, val: (res.data.filters !== null && res.data.filters !== undefined && res.data.filters.length > 0) });
            },
            err => dispatch({ type: searchSubjectsFailed, error: err })
        );
    }
    else {
        dispatch({ type: searchSubjectsFailed, error: `Unknown search type '${searchType}'` });
    }
}

function initLiensSearch(dispatch) {
    dispatch({ type: initLiensSearchRequest });
    apiGet(`${window.babylonApiUrl}/api/search/liens-subject-types`, {},
        res => dispatch({ type: initLiensSearchSucceeded, data: res }),
        err => dispatch({ type: initLiensSearchFailed, error: err })
    );
}

function searchLiens(params, dispatch, getState) {
    dispatch({ type: searchLiensRequest, params: params });
    apiPost(`${window.babylonApiUrl}/api/search/liens`, null, params,
        res => dispatch({ type: searchLiensSucceeded, data: res }),
        err => dispatch({ type: searchLiensFailed, error: err })
    );
}

function exportLiensSearchPdf(getState) {
    const state = getState().search;
    const params = { ...state.searchParams, all: state.exportSettings.all, ids: state.exportSettings.ids };

    if (!isNullOrUndefined(params)) {
        apiPost(`${getHostApi()}/api/search/liens/pdf-export`, null, params);
    }
}

function initBankruptciesSearch(dispatch) {
    dispatch({ type: initBankruptciesSearchRequest });
}

function searchBankruptcies(params, dispatch, getState) {
    dispatch({ type: searchBankruptciesRequest, params: params });
    apiPost(`${window.babylonApiUrl}/api/search/bankruptcies`, null, params,
        res => dispatch({ type: searchBankruptciesSucceeded, data: res }),
        err => dispatch({ type: searchBankruptciesFailed, error: err })
    );
}

function exportBankruptciesSearchPdf(getState) {
    const state = getState().search;
    const params = { ...state.searchParams, all: false, ids: state.exportSettings.ids };

    if (!isNullOrUndefined(params)) {
        apiPost(`${getHostApi()}/api/search/bankruptcies/pdf-export`, null, params);
    }
}

function searchBusinessBulletins(entityId, params, paging, dispatch, getState) {
    const data = { ...params, page: paging.page, pageSize: paging.count }

    dispatch({ type: searchBulletinsRequest, params: data, paging: paging });
    let link;
    if (entityId === null || entityId === undefined || entityId === 0)
        link = `${window.babylonApiUrl}/api/search/bulletin`;
    else
        link = `${window.babylonApiUrl}/api/search/bulletin/${entityId}`;

    apiPost(link, null, data,
        res => dispatch({ type: searchBulletinsSucceeded, data: res }),
        err => dispatch({ type: searchBulletinsFailed, error: err })
    );
}

function exportBusinessBulletinSearchPdf(entityId, getState) {
    const state = getState().search;
    const data = { ...state.searchParams, ...state.searchPaging, all: false, ids: state.exportSettings.ids };

    if (!isNullOrUndefined(data)) {
        let link;
        if (entityId === null || entityId === undefined || entityId === 0)
            link = `${getHostApi()}/api/search/bulletin/pdf-export`;
        else
            link = `${getHostApi()}/api/search/bulletin/${entityId}/pdf-export`;

        apiPost(link, null, data);
    }
}

function performCustomSearch(controller, url, method, data, searchType, dispatch) {
    dispatch({ type: customSearchRequest, searchType: searchType });
    if (method.toUpperCase() === "GET") {
        apiGet(`${window.babylonApiUrl}/api/${controller}/${url}`, {},
            res => dispatch({ type: customSearchSucceeded, data: res, searchType: searchType, }),
            err => dispatch({ type: customSearchFailed, error: err, searchType: searchType })
        );
    }
    else if (method.toUpperCase() === "POST") {
        apiPost(`${window.babylonApiUrl}/api/${controller}/${url}`, {}, data,
            res => dispatch({ type: customSearchSucceeded, data: res, searchType: searchType, }),
            err => dispatch({ type: customSearchFailed, error: err, searchType: searchType })
        );
    }
    else {
        dispatch({ type: customSearchFailed, error: `Unsupported method '${method}'`, searchType: searchType })
    }
}

function performCuzkSearch(params, resultAction, dispatch) {
    dispatch({ type: cuzkSearchRequest, params: params, cuzkSearchType: cuzkSearchType.requests });
    apiPost(`${window.babylonApiUrl}/api/cuzk/search`, null, params,
        res => { 
            if (res.data.isSuccess) {
                dispatch({ type: cuzkSearchSucceeded, data: res, cuzkSearchType: cuzkSearchType.requests }); 
                if (typeof resultAction === "function")
                    resultAction();
            } 
            else dispatch({ type: cuzkSearchFailed, error: res.data, cuzkSearchType: cuzkSearchType.requests });                                  
        },
        err => dispatch({ type: cuzkSearchFailed, error: err, cuzkSearchType: cuzkSearchType.requests })
    );
}

function updateOwnershipDeedSearch(params, resultAction, dispatch) {
    dispatch({ type: cuzkSearchRequest, params: params, cuzkSearchType: cuzkSearchType.requests });
    apiPost(`${window.babylonApiUrl}/api/cuzk/search`, null, params,
        res => {
            if (res.data.isSuccess) {
                dispatch({ type: cuzkSearchSucceeded, data: res, cuzkSearchType: cuzkSearchType.requests });
                params.id = res.data.requestId;
                if (typeof resultAction === "function")
                    resultAction(res.data.requestId);
            }
            else dispatch({ type: cuzkSearchFailed, error: res.data, cuzkSearchType: cuzkSearchType.requests });
        },
        err => dispatch({ type: cuzkSearchFailed, error: err, cuzkSearchType: cuzkSearchType.requests })
    );
}


function performCuzkGetRequest(params, resultAction, reportAction, dispatch) {
    dispatch({ type: cuzkSearchRequest, params: params, cuzkSearchType: cuzkSearchType.subjects });
    apiGet(`${window.babylonApiUrl}/api/cuzk/requests/${params.id}`, params, 
        res => {
            if (res.error) {
                dispatch({ type: cuzkSearchFailed, error: res.error, cuzkSearchType: cuzkSearchType.subjects })
            } else {
                dispatch({ type: cuzkSearchSucceeded, data: res, cuzkSearchType: cuzkSearchType.subjects });
                if (res.data.persons.length === 1 && res.data.persons[0].matchType === 3 && typeof reportAction === 'function')
                    reportAction(`?osId=${res.data.persons[0].id}&eId=${res.data.evidenceId}` + (isNullOrUndefined(res.data.dataDate) ? "" : `&dataDate=${res.data.dataDate}`));
                else if (typeof resultAction === "function")
                    resultAction();
            }
        },
        err => dispatch({ type: cuzkSearchFailed, error: err, cuzkSearchType: cuzkSearchType.subjects })
    );
}

function performCuzkGetOwnershipDeedRequest(params, dispatch) {
    dispatch({ type: cuzkSearchRequest, params: params, cuzkSearchType: cuzkSearchType.ownershipDeed });
    apiGet(`${window.babylonApiUrl}/api/cuzk/requests-ownership-deed/${params.id}`, null,
        res => {
            if (res.error) {
                dispatch({ type: cuzkSearchFailed, error: res.error, cuzkSearchType: cuzkSearchType.ownershipDeed })
            } else {
                if (isNullOrUndefined(res.data.exception))
                    dispatch({ type: cuzkSearchSucceeded, data: res, expandedData: initCuzkGetOwnershipExpandedData(res.data), expandedDataDetail: initCuzkOwnershipDetailExpandedData(res.data.cadastreUnits[0].ownershipDocuments[0]), cuzkSearchType: cuzkSearchType.ownershipDeed, documentId: res.data.cadastreUnits[0].ownershipDocuments[0].telId });
                else
                    dispatch({ type: cuzkSearchSucceeded, data: res, expandedData: null, expandedDataDetail: null, cuzkSearchType: cuzkSearchType.ownershipDeed, documentId: null });
            }
        },
        err => dispatch({ type: cuzkSearchFailed, error: err, cuzkSearchType: cuzkSearchType.ownershipDeed })
    );
}

function performCuzkGetOwnershipDetailAsReport(params, useCache, dispatch) {
    if (isNullOrUndefined(params) || isNullOrUndefined(params.telId) || isNullOrUndefined(params.ownershipDocumentId) || isNullOrUndefined(params.osId) || isNullOrUndefined(params.cadastreUnitId))
        throw "Parameters must contain non-null properties 'telId', 'ownershipDocumentId', 'osId', 'cadastreUnitId'";
    dispatch({ type: cuzkSearchRequest, params: params, cuzkSearchType: cuzkSearchType.ownershipDeed });
    apiGet(`${window.babylonApiUrl}/api/cuzk/ownership-document-report`, { ...params, useCache: useCache },
        res => {
            if (isNullOrUndefined(res.data.exception))
                dispatch({ type: cuzkSearchSucceeded, data: res, expandedData: initCuzkGetOwnershipExpandedData(res.data), expandedDataDetail: initCuzkOwnershipDetailExpandedData(res.data.cadastreUnits[0].ownershipDocuments[0]), cuzkSearchType: cuzkSearchType.ownershipDeed, documentId: res.data.cadastreUnits[0].ownershipDocuments[0].telId });
            else
                dispatch({ type: cuzkSearchSucceeded, data: res, expandedData: null, expandedDataDetail: null, cuzkSearchType: cuzkSearchType.ownershipDeed, documentId: null });
        },
        err => dispatch({ type: cuzkSearchFailed, error: err, cuzkSearchType: cuzkSearchType.ownershipDeed })
    );
}

function initCuzkGetOwnershipExpandedData(data) {
    if (!isNullOrUndefined(data.exception))
        return {};
    var result = {};
    result[data.cadastreUnits[0].cadastreUnitId] = {
        expanded: true, [cuzkExpandType.ownershipDocuments]: { [data.cadastreUnits[0].ownershipDocuments[0].telId]: true}
    };
    return result;
}


function performCuzkOwnershipSearch(params, useCache, dispatch) {
    dispatch({ type: cuzkSearchRequest, params: params, cuzkSearchType: cuzkSearchType.report });
    apiGet(`${window.babylonApiUrl}/api/cuzk/ownership-overview`, { ...params, useCache: useCache },
        res => dispatch({ type: cuzkSearchSucceeded, data: res, expandedData: initCuzkOwnershipExpandedData(res.data), cuzkSearchType: cuzkSearchType.report }),
        err => dispatch({ type: cuzkSearchFailed, error: err, cuzkSearchType: cuzkSearchType.report })
    );
}

function initCuzkOwnershipExpandedData(data) {
    if (!isNullOrUndefined(data.exception))
        return {};
    return {
        ...data.cadastreUnits.items.map(x => ({
            [x.cadastreUnitId]: {
                expanded: false,
                [cuzkExpandType.ownershipDocuments]: { ...x.ownershipDocuments.map(y => ({ [y.telId]: false })) }
            }
        }))
    };
}

function performCuzkOwnershipDetailSearch(params, useCache, dispatch) {
    if (params === undefined || params === null || params.telId === undefined || params.telId === null)
        throw "Parameters must contain non-null property 'telId'";
    dispatch({ type: cuzkSearchRequest, params: params, cuzkSearchType: cuzkSearchType.detail, documentId: params.telId });
    apiGet(`${window.babylonApiUrl}/api/cuzk/ownership-document`, { ...params, useCache: useCache },
        res => dispatch({ type: cuzkSearchSucceeded, data: res, expandedData: initCuzkOwnershipDetailExpandedData(res.data), cuzkSearchType: cuzkSearchType.detail, documentId: params.telId }),
        err => dispatch({ type: cuzkSearchFailed, error: err, cuzkSearchType: cuzkSearchType.detail, documentId: params.telId })
    );
}

function initCuzkOwnershipDetailExpandedData(data) {
    return {
        [cuzkExpandType.parcels]: Object.assign({}, ...data.parcels.map(x => ({ [x.id]: true }))),
        [cuzkExpandType.buildings]: Object.assign({}, ...data.buildings.map(x => ({ [x.id]: true }))),
        [cuzkExpandType.flats]: Object.assign({}, ...data.flats.map(x => ({ [x.id]: true })))
    }
}

function performCuzkExportReportPdf(type, isSearchOwnershipDeed, getState, dispatch) {
    const cuzkState = getState().search.cuzk;
    const reportType = cuzkSearchType.report;
    const reportState = cuzkState[reportType];
    let params = isNullOrUndefined(reportState) ? null : reportState["params"];
    const isOwnershipDetailAsReport = isNullOrUndefined(params.id) && isSearchOwnershipDeed;

    if (isNullOrUndefined(params) || 
        (isNullOrUndefined(params.osId) && (!isSearchOwnershipDeed || isOwnershipDetailAsReport)) || 
        (isNullOrUndefined(params.id) && isSearchOwnershipDeed && !isOwnershipDetailAsReport) || 
        ((isNullOrUndefined(params.telId) || isNullOrUndefined(params.ownershipDocumentId) || isNullOrUndefined(params.cadastreUnitId)) && isOwnershipDetailAsReport)) {
        dispatch({ type: cuzkPdfError, message: "UnhappyUser.InvalidInputParameters" });
        return;
    }

    let reportExpandedData = {};
    let detailExpandedData = {};

    if (type === cuzkExportPdfType.expanded) {
        reportExpandedData = isNullOrUndefined(getState().search.cuzk[reportType]) ? null : reportState["expandedData"];
        const detailData = isNullOrUndefined(getState().search.cuzk[cuzkSearchType.detail]) ? null : cuzkState[cuzkSearchType.detail];
        detailExpandedData = Object.assign({}, ...Object.keys(detailData).map(k => ({ [k]: detailData[k].expandedData })));
    }

    const method = isOwnershipDetailAsReport ? 'pdf-ownership-document-report-export' : isSearchOwnershipDeed ? 'pdf-search-ownership-deed-export' : 'pdf-report-export';
    const exportParams = { exportType: type, reportExpanded: reportExpandedData, detailExpanded: detailExpandedData };
    apiPost(`${getHostApi()}/api/cuzk/report/${method}`, params, exportParams,
        (s) => {
            if (!isNullOrUndefined(s.error))
                dispatch({ type: cuzkPdfError });
        },
        (e) => {
            writeErr(e);
            dispatch({ type: cuzkPdfError });
        }
    );
}

function performCuzkExportSearchResultPdf(getState, dispatch) {
    const cuzkState = getState().search.cuzk;
    const reportState = cuzkState[cuzkSearchType.subjects];
    let params = isNullOrUndefined(reportState) ? null : reportState["params"];
    if (isNullOrUndefined(params)) {
        dispatch({ type: cuzkPdfError, message: "UnhappyUser.InvalidInputParameters" });
        return;
    }

    apiPost(`${getHostApi()}/api/cuzk/report/pdf-search-result-export`, params, null,
        (s) => {
            if (!isNullOrUndefined(s.error))
                dispatch({ type: cuzkPdfError });
        },
        (e) => {
            writeErr(e);
            dispatch({ type: cuzkPdfError });
        }
    );
}

function performPropertySkSearch(params, onSuccess, dispatch) {
    if (isNullOrUndefined(params) || Object.keys(params).length === 0)
        throw "At least 1 search parameter must be supplied";
    if (isNullOrUndefined(params.searchBy) || params.searchBy.length === 0)
        throw "Parameter 'searchBy' must be supplied";

    const cuId = params.searchBy === propertySkSearchBy.odDetail ? params.cadastreUnitId : undefined;
    dispatch({ type: propertySkSearchRequest, params: params, cadastreUnitId: cuId });

    apiPost(`${window.babylonApiUrl}/api/cadastre-sk/${getPropertySkSearchTarget(params.searchBy)}`, null, { ...params, searchBy: undefined },
        res => {
            dispatch({ type: propertySkSearchSucceeded, data: res, searchBy: params.searchBy, cadastreUnitId: cuId });
            if (typeof onSuccess === "function")
                onSuccess(res);
        },
        err => dispatch({ type: propertySkSearchFailed, error: processError(err), searchBy: params.searchBy, cadastreUnitId: cuId })
    );
}

function getPropertySkSearchTarget(searchBy) {
    switch (parseInt(searchBy)) {
        case propertySkSearchBy.companyOwners: return "search-company-owners";
        case propertySkSearchBy.personOwners: return "search-person-owners";
        case propertySkSearchBy.companyOwnerDeeds: return "search-od-company";
        case propertySkSearchBy.personOwnerDeeds: return "search-od-person";
        case propertySkSearchBy.ownershipDeed: return "search-by-od-number";
        case propertySkSearchBy.registrationNumber: return "search-by-reg-number";
        case propertySkSearchBy.landId: return "search-by-land-id";
        case propertySkSearchBy.flatNumber: return "search-by-flat-number";
        case propertySkSearchBy.document: return "ownership-deed";
        case propertySkSearchBy.documentCompany: return "ownership-deed-company";
        case propertySkSearchBy.documentPerson: return "ownership-deed-person";
        case propertySkSearchBy.odDetail: return "search-od-detail";
        default: throw "Invalid 'searchBy' parameter value";
    };
}

function performPropertySkExportPdf(withCheck, getState, dispatch) {
    const params = isNullOrUndefined(getState().search.propertySk) ? null : getState().search.propertySk.params;

    if (isNullOrUndefined(params) || Object.keys(params).length === 0)
        throw "At least 1 search parameter must be supplied";
    if (isNullOrUndefined(params.searchBy) || params.searchBy.length === 0)
        throw "Parameter 'searchBy' must be supplied";
    
    const link = `${getHostApi()}/api/cadastre-sk/${getPropertySkSearchTarget(params.searchBy)}/pdf-export`;
    const state = getState().search;
    const searchParams = { ...params, searchBy: undefined, all: state.exportSettings.all, ids: state.exportSettings.ids };

    if (withCheck) {
        apiPost(`${link}-check`, null, searchParams,
            (res) => {
                if (res.data.isSuccess) {
                    //pdf export
                    apiPost(link, null, searchParams, 
                        (res) => {
                            if (res.error) {
                                dispatch({ type: pdfExportError, error: { message: "Common.Error" } });
                            }
                        }, 
                        (err) => dispatch({ type: pdfExportError, error: processError(err) })
                    );
                } else {
                    dispatch({ type: pdfExportError, error: { message: res.data.exception || "Common.Error" } })
                }
            },
            (err) => dispatch({ type: pdfExportError, error: processError(err) })
            
        );   
    } else {
        //pdf export
        apiPost(link, null, searchParams, 
            (res) => {
                if (res.error) {
                    dispatch({ type: pdfExportError, error: { message: "Common.Error" } });
                }
            }, 
            (err) => dispatch({ type: pdfExportError, error: processError(err) })
        );   
    } 
}

function initClipboardCounter(clipboard) {
    if (clipboard.length === 0)
        return 0;

    return Math.max.apply(Math, clipboard.map(function (entityInfo) { return entityInfo.clipboardId })) + 1;
}

function addToClipboardArray(arr, obj, sortId, prop) {
    obj.clipboardId = sortId;
    return addToArray(arr, obj, prop);
}